import { useState, useCallback, useRef } from "react";
import {
  ChevronUp,
  ChevronDown,
  Trash2,
  SplitSquareHorizontal,
  Merge,
  Loader2,
  RotateCcw,
  Sparkles,
  AlertTriangle,
  Upload,
  FileText,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  useBulkCreateItems,
  useAiParseItems,
  useProgramApi,
  useUpdateRegisterSourceFile,
  useExtractSourceFile,
  type AiDraftItem,
  type ExtractionResult,
} from "@/lib/program-registration";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";

// ── Types ─────────────────────────────────────────────────────────────────────

type SplitMode = "headings" | "paragraphs" | "lines" | "one" | "ai";

interface DraftItem {
  id: string;
  title: string;
  /** Maps to plannedContent on save */
  content: string;
  plannedWeek?: string;
  plannedLessonLabel?: string;
  outcomeOrContentDotPoint?: string;
  plannedResources?: string;
  plannedAssessmentCheckpoint?: string;
  /** AI-supplied uncertainty hint — shown read-only in amber */
  parsingNote?: string;
  /** True when the item was produced by AI-assisted import */
  isAiGenerated?: boolean;
}

// ── Rule-based parsing ─────────────────────────────────────────────────────────

const HEADING_RE =
  /^(Week\s+(One|Two|Three|Four|Five|Six|Seven|Eight|Nine|Ten|\d+)|Lesson\s+\d+|Topic\s+\d+|Activity\s+\d+|Unit\s+\d+|Term\s+\d+)/i;

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function parseText(raw: string, mode: Exclude<SplitMode, "ai">): DraftItem[] {
  const text = raw.trim();
  if (!text) return [];

  if (mode === "one") {
    return [{ id: uid(), title: text.split("\n")[0].trim() || "Untitled item", content: text }];
  }

  if (mode === "lines") {
    return text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => ({ id: uid(), title: line, content: "" }));
  }

  if (mode === "paragraphs") {
    return text
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter(Boolean)
      .map((para) => {
        const lines = para.split("\n");
        return {
          id: uid(),
          title: lines[0].trim() || "Untitled item",
          content: lines.slice(1).join("\n").trim(),
        };
      });
  }

  // "headings" mode — auto-detect heading lines
  const lines = text.split("\n");
  const items: DraftItem[] = [];
  let currentTitle = "";
  let currentContent: string[] = [];

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      if (currentTitle) currentContent.push("");
      continue;
    }
    if (HEADING_RE.test(line)) {
      if (currentTitle) {
        items.push({
          id: uid(),
          title: currentTitle,
          content: currentContent.join("\n").trim(),
        });
      }
      currentTitle = line;
      currentContent = [];
    } else {
      if (currentTitle) {
        currentContent.push(rawLine);
      } else {
        currentTitle = line;
        currentContent = [];
      }
    }
  }

  if (currentTitle) {
    items.push({ id: uid(), title: currentTitle, content: currentContent.join("\n").trim() });
  }

  // Fallback: if no headings detected, fall through to paragraph mode
  if (items.length === 0 && text.length > 0) {
    return parseText(text, "paragraphs");
  }

  return items;
}

// ── Table-aware paste utilities ───────────────────────────────────────────────

/**
 * Extract text from a single table cell, collapsing internal whitespace so
 * multi-paragraph Word cells become a single readable line.
 */
function cleanCellText(cell: Element): string {
  return (cell.textContent ?? "").replace(/\s+/g, " ").trim();
}

/**
 * Try to extract structured text from HTML table(s) found in pasted content.
 * Converts each data row to a "Column: value" block so the AI (and the
 * teacher) can see which column each piece of content came from.
 * Returns null when no usable tables are found.
 */
function extractHtmlTables(html: string): string | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const tables = Array.from(doc.querySelectorAll("table"));
  if (tables.length === 0) return null;

  const allBlocks: string[] = [];

  for (const table of tables) {
    const rows = Array.from(table.querySelectorAll("tr")).filter((r) =>
      r.querySelector("td, th"),
    );
    if (rows.length === 0) continue;

    // Detect a merged-cell title row (e.g. "Year 5 English — Term 3 Program")
    // spanning the full width — skip it and look for headers in the next row.
    let dataStartIdx = 0;
    let headerRow: string[] = [];

    const firstRowCells = Array.from(rows[0].querySelectorAll("td, th"));
    const isMergedTitle =
      firstRowCells.length === 1 &&
      parseInt(firstRowCells[0].getAttribute("colspan") ?? "1", 10) > 1;

    if (isMergedTitle && rows.length > 1) {
      // Second row is likely the real header
      headerRow = Array.from(rows[1].querySelectorAll("td, th")).map(cleanCellText);
      dataStartIdx = 2;
    } else {
      const firstTexts = firstRowCells.map(cleanCellText);
      const hasThCells = rows[0].querySelectorAll("th").length > 0;
      const cellsAreShort = firstTexts.every((t) => t.length <= 80);
      const looksLikeHeader =
        hasThCells || (cellsAreShort && rows.length > 1 && firstTexts.some(Boolean));

      if (looksLikeHeader) {
        headerRow = firstTexts;
        dataStartIdx = 1;
      }
    }

    const dataRows = rows.slice(dataStartIdx);
    let rowNum = 0;

    for (const row of dataRows) {
      const cells = Array.from(row.querySelectorAll("td, th")).map(cleanCellText);
      if (cells.every((c) => !c)) continue; // skip visually blank rows

      rowNum++;
      const lines = [`Row ${rowNum}:`];
      cells.forEach((cell, i) => {
        if (!cell) return;
        const header = headerRow[i] || `Column ${i + 1}`;
        lines.push(`${header}: ${cell}`);
      });
      allBlocks.push(lines.join("\n"));
    }
  }

  return allBlocks.length > 0 ? allBlocks.join("\n\n") : null;
}

/**
 * Try to parse tab-separated text (TSV) as a table — common when copying from
 * Excel, Google Sheets, or Word tables without HTML being available.
 * Returns null when the text doesn't look like TSV.
 */
function extractTsvText(text: string): string | null {
  const lines = text.split("\n").filter((l) => l.trim());
  const tabLines = lines.filter((l) => l.includes("\t"));
  if (tabLines.length < 2) return null; // need header + at least one data row

  const headers = tabLines[0].split("\t").map((h) => h.trim());
  const dataLines = tabLines.slice(1);

  const blocks: string[] = [];
  let rowNum = 0;

  for (const line of dataLines) {
    const cells = line.split("\t").map((c) => c.trim());
    if (cells.every((c) => !c)) continue;

    rowNum++;
    const rowLines = [`Row ${rowNum}:`];
    cells.forEach((cell, i) => {
      if (!cell) return;
      const header = headers[i] || `Column ${i + 1}`;
      rowLines.push(`${header}: ${cell}`);
    });
    blocks.push(rowLines.join("\n"));
  }

  return blocks.length > 0 ? blocks.join("\n\n") : null;
}

// ── Split mode config ─────────────────────────────────────────────────────────

const RULE_BASED_MODES: {
  value: Exclude<SplitMode, "ai">;
  label: string;
  description: string;
}[] = [
  {
    value: "headings",
    label: "Headings / week blocks",
    description: "Auto-detect Week 1, Lesson 2, Topic 3… as item titles",
  },
  {
    value: "paragraphs",
    label: "Paragraph chunks",
    description: "Each blank-line-separated block becomes one item",
  },
  {
    value: "lines",
    label: "Each line",
    description: "Every non-empty line becomes a separate item",
  },
  {
    value: "one",
    label: "Keep as one item",
    description: "Entire pasted text becomes a single program item",
  },
];

// ── Draft item row ─────────────────────────────────────────────────────────────

interface DraftItemRowProps {
  item: DraftItem;
  index: number;
  total: number;
  onChange: (id: string, patch: Partial<DraftItem>) => void;
  onDelete: (id: string) => void;
  onMoveUp: (id: string) => void;
  onMoveDown: (id: string) => void;
  onSplit: (id: string) => void;
  onMergeDown: (id: string) => void;
}

function DraftItemRow({
  item,
  index,
  total,
  onChange,
  onDelete,
  onMoveUp,
  onMoveDown,
  onSplit,
  onMergeDown,
}: DraftItemRowProps) {
  const [expanded, setExpanded] = useState(false);

  const hasRichContent =
    item.content ||
    item.plannedWeek ||
    item.plannedLessonLabel ||
    item.outcomeOrContentDotPoint ||
    item.plannedResources ||
    item.plannedAssessmentCheckpoint ||
    item.parsingNote;

  const collapsedPreview =
    item.content ||
    (item.plannedResources ? `Resources: ${item.plannedResources}` : "") ||
    (item.outcomeOrContentDotPoint ? `Outcome: ${item.outcomeOrContentDotPoint}` : "") ||
    (item.plannedWeek || item.plannedLessonLabel
      ? [item.plannedWeek, item.plannedLessonLabel].filter(Boolean).join(" · ")
      : "");

  return (
    <div className="border rounded-lg bg-background">
      <div className="flex items-start gap-2 p-3">
        {/* Order number */}
        <span className="flex-shrink-0 w-6 text-center text-xs text-muted-foreground font-mono pt-2">
          {index + 1}
        </span>

        {/* Title + content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 min-w-0">
            <Input
              value={item.title}
              onChange={(e) => onChange(item.id, { title: e.target.value })}
              placeholder="Item title"
              className="text-sm font-medium border-0 bg-transparent p-0 h-auto focus-visible:ring-0 shadow-none flex-1 min-w-0"
            />
            {item.isAiGenerated && (
              <span className="flex-shrink-0 text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5 font-medium leading-none">
                AI
              </span>
            )}
          </div>

          {!expanded && collapsedPreview && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs text-muted-foreground hover:text-foreground mt-0.5 truncate max-w-full block text-left"
            >
              {collapsedPreview.slice(0, 80)}
              {collapsedPreview.length > 80 ? "…" : ""}
            </button>
          )}

          {!expanded && !hasRichContent && (
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="text-xs text-muted-foreground hover:text-foreground mt-0.5"
            >
              Add details…
            </button>
          )}

          {expanded && (
            <div className="mt-2 space-y-2.5">
              {/* Planned content / activities */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Planned content / activities</Label>
                <Textarea
                  value={item.content}
                  onChange={(e) => onChange(item.id, { content: e.target.value })}
                  placeholder="Teaching and learning activities…"
                  rows={3}
                  className="text-sm resize-none"
                />
              </div>

              {/* Week + lesson label */}
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Week</Label>
                  <Input
                    value={item.plannedWeek ?? ""}
                    onChange={(e) =>
                      onChange(item.id, { plannedWeek: e.target.value || undefined })
                    }
                    placeholder="e.g. Week 1"
                    className="text-sm h-8"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Lesson label</Label>
                  <Input
                    value={item.plannedLessonLabel ?? ""}
                    onChange={(e) =>
                      onChange(item.id, { plannedLessonLabel: e.target.value || undefined })
                    }
                    placeholder="e.g. Lesson 1"
                    className="text-sm h-8"
                  />
                </div>
              </div>

              {/* Outcome / content dot point */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Outcome / content dot point
                </Label>
                <Textarea
                  value={item.outcomeOrContentDotPoint ?? ""}
                  onChange={(e) =>
                    onChange(item.id, {
                      outcomeOrContentDotPoint: e.target.value || undefined,
                    })
                  }
                  placeholder="Syllabus outcomes or content descriptors…"
                  rows={2}
                  className="text-sm resize-none"
                />
              </div>

              {/* Resources */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Resources</Label>
                <Textarea
                  value={item.plannedResources ?? ""}
                  onChange={(e) =>
                    onChange(item.id, { plannedResources: e.target.value || undefined })
                  }
                  placeholder="Materials, textbooks, worksheets, tools…"
                  rows={2}
                  className="text-sm resize-none"
                />
              </div>

              {/* Assessment / checkpoint */}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  Assessment / checkpoint
                </Label>
                <Textarea
                  value={item.plannedAssessmentCheckpoint ?? ""}
                  onChange={(e) =>
                    onChange(item.id, {
                      plannedAssessmentCheckpoint: e.target.value || undefined,
                    })
                  }
                  placeholder="Assessment tasks or evidence of learning…"
                  rows={2}
                  className="text-sm resize-none"
                />
              </div>

              {/* Parsing note — AI-supplied uncertainty hint, read-only */}
              {item.parsingNote && (
                <div className="flex gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>{item.parsingNote}</span>
                </div>
              )}

              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="text-xs text-muted-foreground hover:text-foreground"
              >
                Collapse
              </button>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            type="button"
            title="Move up"
            disabled={index === 0}
            onClick={() => onMoveUp(item.id)}
            className="p-1.5 rounded hover:bg-muted disabled:opacity-30 transition-colors"
          >
            <ChevronUp className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Move down"
            disabled={index === total - 1}
            onClick={() => onMoveDown(item.id)}
            className="p-1.5 rounded hover:bg-muted disabled:opacity-30 transition-colors"
          >
            <ChevronDown className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            title="Split at first line break"
            onClick={() => onSplit(item.id)}
            className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <SplitSquareHorizontal className="h-3.5 w-3.5" />
          </button>
          {index < total - 1 && (
            <button
              type="button"
              title="Merge with next item"
              onClick={() => onMergeDown(item.id)}
              className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <Merge className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            title="Delete item"
            onClick={() => onDelete(item.id)}
            className="p-1.5 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

interface ImportReviewProps {
  open: boolean;
  onClose: () => void;
  registerId: number;
  existingItemCount: number;
  /** If the register already has a source file stored, pass it here so the
   *  dialog can offer one-click extraction without re-uploading. */
  existingSourceFile?: { fileName: string; objectKey: string } | null;
}

type Step = "paste" | "review";

export function ImportReview({
  open,
  onClose,
  registerId,
  existingItemCount,
  existingSourceFile,
}: ImportReviewProps) {
  const { toast } = useToast();
  const bulkCreate = useBulkCreateItems(registerId);
  const aiParseItems = useAiParseItems(registerId);

  const apiFetch = useProgramApi();
  const updateSourceFile = useUpdateRegisterSourceFile(registerId);
  const extractSourceFile = useExtractSourceFile(registerId);

  const [step, setStep] = useState<Step>("paste");
  const [pastedText, setPastedText] = useState("");
  const [splitMode, setSplitMode] = useState<SplitMode>("headings");
  const [draftItems, setDraftItems] = useState<DraftItem[]>([]);
  const [pasteWarning, setPasteWarning] = useState<string | null>(null);

  // ── Source file upload state (Stage 3A2) ─────────────────────────────────
  const [isExtractingExisting, setIsExtractingExisting] = useState(false);
  const [uploadedFile, setUploadedFile] = useState<{
    fileName: string;
    objectPath: string;
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [tableWarning, setTableWarning] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Source file upload + extraction (Stage 3A2) ───────────────────────────

  const PROGRAM_DOC_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

  async function handleFileChange(
    e: React.ChangeEvent<HTMLInputElement>,
  ) {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate extension
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (ext === "pdf") {
      toast({
        title: "PDF upload is not supported yet",
        description:
          "For best results, upload a DOCX file or paste your program text.",
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    if (ext !== "docx") {
      toast({
        title: "Unsupported file type",
        description: "Please upload a Word (.docx) file.",
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    // Validate size
    if (file.size > PROGRAM_DOC_MAX_BYTES) {
      toast({
        title: "File too large",
        description: "Program documents must be smaller than 10 MB.",
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setIsUploading(true);
    setTableWarning(false);
    setUploadedFile(null);

    try {
      // Step 1: Request presigned upload URL from the API
      const { uploadURL, objectPath } = await apiFetch<{
        uploadURL: string;
        objectPath: string;
      }>("/api/storage/uploads/request-program-doc-url", {
        method: "POST",
        body: JSON.stringify({ name: file.name, size: file.size }),
      });

      // Step 2: Upload directly to GCS (no auth header — presigned URL)
      const uploadRes = await fetch(uploadURL, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type":
            file.type ||
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        },
      });
      if (!uploadRes.ok) throw new Error("Upload to storage failed.");

      // Step 3: Persist the objectKey + fileName on the register
      await updateSourceFile.mutateAsync({
        sourceFileObjectKey: objectPath,
        sourceFileName: file.name,
      });

      setUploadedFile({ fileName: file.name, objectPath });
      setIsUploading(false);
      setIsExtracting(true);

      // Step 4: Auto-extract content (no AI involved — just text extraction)
      const result: ExtractionResult = await extractSourceFile.mutateAsync();

      setPastedText(result.structuredText);
      setTableWarning(result.tableWarning);
      if (pasteWarning) setPasteWarning(null);
    } catch {
      // api-error-helper-exempt: no error object available (bare catch); this
      // catches a local object-storage / presigned-upload failure and shows
      // environment-specific fallback guidance rather than a server ApiError.
      toast({
        title: "File upload failed",
        description: "File upload is unavailable in this environment. Please paste your program content in the text area below instead.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setIsExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  function handleRemoveSourceFile() {
    setUploadedFile(null);
    setTableWarning(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  async function handleExtractExisting() {
    setIsExtractingExisting(true);
    setTableWarning(false);
    try {
      const result: ExtractionResult = await extractSourceFile.mutateAsync();
      setPastedText(result.structuredText);
      setTableWarning(result.tableWarning);
      if (pasteWarning) setPasteWarning(null);
    } catch (err: unknown) {
      toast({
        title: "Extraction failed",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsExtractingExisting(false);
    }
  }

  // ── Paste event — table-aware ─────────────────────────────────────────────

  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    const html = e.clipboardData.getData("text/html");
    const plain = e.clipboardData.getData("text/plain");

    if (html) {
      // 1. Try to extract HTML tables first (Word, Google Docs, etc.)
      const tableText = extractHtmlTables(html);
      if (tableText) {
        e.preventDefault();
        setPastedText(tableText);
        setPasteWarning(null);
        return;
      }

      // 2. HTML was present but contained no recoverable tables — warn if
      //    tables were in the source but couldn't be extracted.
      if (/<table/i.test(html)) {
        setPasteWarning(
          "Some table formatting may have been lost. For best results, upload your original Word (.docx) program file.",
        );
      }
    }

    // 3. Try TSV (tab-separated) — works when HTML isn't available or tables
    //    were copied from Excel / Google Sheets.
    const textToCheck = plain || "";
    if (textToCheck.includes("\t")) {
      const tsvText = extractTsvText(textToCheck);
      if (tsvText) {
        e.preventDefault();
        setPastedText(tsvText);
        setPasteWarning(null);
        return;
      }
    }

    // 4. Default — let the browser paste plain text as normal.
  }

  // ── Rule-based parse ───────────────────────────────────────────────────────

  function handleParse() {
    if (splitMode === "ai") return; // handled separately by handleAiParse
    const parsed = parseText(pastedText, splitMode);
    if (parsed.length === 0) {
      toast({
        title: "Nothing to import",
        description: "Paste some text first.",
        variant: "destructive",
      });
      return;
    }
    setDraftItems(parsed);
    setStep("review");
  }

  // ── AI-assisted parse ──────────────────────────────────────────────────────

  function handleAiParse() {
    const text = pastedText.trim();
    if (!text) {
      toast({
        title: "Nothing to analyse",
        description: "Paste some text first.",
        variant: "destructive",
      });
      return;
    }
    aiParseItems.mutate(text, {
      onSuccess: (data) => {
        const items: DraftItem[] = data.items.map((ai: AiDraftItem) => ({
          id: uid(),
          title: ai.title,
          content: ai.plannedContent ?? "",
          plannedWeek: ai.plannedWeek ?? undefined,
          plannedLessonLabel: ai.plannedLessonLabel ?? undefined,
          outcomeOrContentDotPoint: ai.outcomeOrContentDotPoint ?? undefined,
          plannedResources: ai.plannedResources ?? undefined,
          plannedAssessmentCheckpoint: ai.plannedAssessmentCheckpoint ?? undefined,
          parsingNote: ai.parsingNote ?? undefined,
          isAiGenerated: true,
        }));
        if (items.length === 0) {
          toast({
            title: "No items found",
            description:
              "The AI could not identify any items. Try a rule-based import instead.",
            variant: "destructive",
          });
          return;
        }
        setDraftItems(items);
        setStep("review");
      },
      onError: (err: unknown) => {
        const message = getApiErrorMessage(
          err,
          "AI analysis failed. Try a rule-based import instead.",
        );
        toast({ title: "AI analysis failed", description: message, variant: "destructive" });
      },
    });
  }

  // ── Review handlers ────────────────────────────────────────────────────────

  function handleReset() {
    setStep("paste");
    setDraftItems([]);
    setPasteWarning(null);
    setTableWarning(false);
    setUploadedFile(null);
    aiParseItems.reset();
  }

  const handleChange = useCallback(
    (id: string, patch: Partial<DraftItem>) => {
      setDraftItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
      );
    },
    [],
  );

  const handleDelete = useCallback((id: string) => {
    setDraftItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const handleMoveUp = useCallback((id: string) => {
    setDraftItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx <= 0) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  }, []);

  const handleMoveDown = useCallback((id: string) => {
    setDraftItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  }, []);

  const handleSplit = useCallback(
    (id: string) => {
      setDraftItems((prev) => {
        const idx = prev.findIndex((i) => i.id === id);
        if (idx < 0) return prev;
        const item = prev[idx];
        const combined = [item.title, item.content].filter(Boolean).join("\n");
        const lines = combined.split("\n");
        if (lines.length <= 1) {
          toast({ title: "Nothing to split", description: "Item has only one line." });
          return prev;
        }
        // First split item inherits rich fields; second starts clean
        const itemA: DraftItem = {
          id: uid(),
          title: lines[0].trim(),
          content: "",
          plannedWeek: item.plannedWeek,
          plannedLessonLabel: item.plannedLessonLabel,
          outcomeOrContentDotPoint: item.outcomeOrContentDotPoint,
          plannedResources: item.plannedResources,
          plannedAssessmentCheckpoint: item.plannedAssessmentCheckpoint,
          isAiGenerated: item.isAiGenerated,
        };
        const itemB: DraftItem = {
          id: uid(),
          title: lines[1].trim() || "Continuation",
          content: lines.slice(2).join("\n").trim(),
        };
        const next = [...prev];
        next.splice(idx, 1, itemA, itemB);
        return next;
      });
    },
    [toast],
  );

  const handleMergeDown = useCallback((id: string) => {
    setDraftItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx < 0 || idx >= prev.length - 1) return prev;
      const a = prev[idx];
      const b = prev[idx + 1];
      const merged: DraftItem = {
        id: a.id,
        title: a.title,
        // Concatenate content from both; b's title becomes part of content
        content: [a.content, b.title, b.content].filter(Boolean).join("\n"),
        // Week / lesson: prefer first item's value
        plannedWeek: a.plannedWeek || b.plannedWeek || undefined,
        plannedLessonLabel: a.plannedLessonLabel || b.plannedLessonLabel || undefined,
        // Text fields: concatenate both
        outcomeOrContentDotPoint:
          [a.outcomeOrContentDotPoint, b.outcomeOrContentDotPoint]
            .filter(Boolean)
            .join("\n") || undefined,
        plannedResources:
          [a.plannedResources, b.plannedResources].filter(Boolean).join("\n") || undefined,
        plannedAssessmentCheckpoint:
          [a.plannedAssessmentCheckpoint, b.plannedAssessmentCheckpoint]
            .filter(Boolean)
            .join("\n") || undefined,
        isAiGenerated: a.isAiGenerated || b.isAiGenerated,
      };
      const next = [...prev];
      next.splice(idx, 2, merged);
      return next;
    });
  }, []);

  // ── Save ──────────────────────────────────────────────────────────────────

  function handleConfirm() {
    const valid = draftItems.filter((i) => i.title.trim());
    if (valid.length === 0) {
      toast({
        title: "No valid items",
        description: "Every item needs a title.",
        variant: "destructive",
      });
      return;
    }

    const offset = existingItemCount;
    bulkCreate.mutate(
      valid.map((item, idx) => ({
        title: item.title.trim(),
        plannedContent: item.content.trim() || null,
        plannedOrder: offset + idx,
        plannedWeek: item.plannedWeek?.trim() || null,
        plannedLessonLabel: item.plannedLessonLabel?.trim() || null,
        outcomeOrContentDotPoint: item.outcomeOrContentDotPoint?.trim() || null,
        plannedResources: item.plannedResources?.trim() || null,
        plannedAssessmentCheckpoint: item.plannedAssessmentCheckpoint?.trim() || null,
      })),
      {
        onSuccess: (data) => {
          toast({ title: `${data.items.length} items imported` });
          handleClose();
        },
        onError: (err: unknown) => {
          toast({
            title: "Import failed",
            description: getApiErrorMessage(err, "Please try again."),
            variant: "destructive",
          });
        },
      },
    );
  }

  function handleClose() {
    setStep("paste");
    setPastedText("");
    setDraftItems([]);
    setPasteWarning(null);
    setTableWarning(false);
    setUploadedFile(null);
    setIsUploading(false);
    setIsExtracting(false);
    aiParseItems.reset();
    onClose();
  }

  const isAiMode = splitMode === "ai";
  const canProceed = !!pastedText.trim();

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === "paste"
              ? "Import program items"
              : `Review ${draftItems.length} item${draftItems.length !== 1 ? "s" : ""}`}
          </DialogTitle>
        </DialogHeader>

        {step === "paste" ? (
          <div className="space-y-5 pt-1">
            {/* Source file upload — Stage 3A2 */}
            <div className="space-y-1.5">
              <Label>Upload source program (optional)</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                className="sr-only"
                onChange={handleFileChange}
                disabled={isUploading || isExtracting}
                aria-hidden="true"
              />
              {uploadedFile ? (
                <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">
                  <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate text-sm">
                    {uploadedFile.fileName}
                  </span>
                  {isExtracting ? (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Extracting…</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 px-2 text-xs"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isUploading || isExtracting}
                      >
                        Replace
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        onClick={handleRemoveSourceFile}
                        aria-label="Remove source file"
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : existingSourceFile ? (
                <div className="rounded-lg border bg-muted/40 px-3 py-2.5 space-y-2">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate text-sm">
                      {existingSourceFile.fileName}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 px-2 text-xs flex-shrink-0"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isExtractingExisting}
                    >
                      Replace
                    </Button>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="w-full h-8 text-xs"
                    onClick={handleExtractExisting}
                    disabled={isExtractingExisting}
                  >
                    {isExtractingExisting ? (
                      <>
                        <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        Extracting…
                      </>
                    ) : (
                      "Extract content from uploaded file"
                    )}
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="flex w-full flex-col items-center gap-1.5 rounded-lg border border-dashed border-input px-4 py-5 transition-colors hover:border-primary/50 hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || isExtracting}
                >
                  {isUploading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Uploading…</span>
                    </div>
                  ) : (
                    <>
                      <Upload className="h-5 w-5 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        Upload Word program
                      </span>
                      <span className="text-xs text-muted-foreground">
                        .docx · max 10 MB · tables are preserved automatically
                      </span>
                    </>
                  )}
                </button>
              )}
              {tableWarning && (
                <div className="flex gap-1.5 rounded border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-700">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    Some table formatting may not have imported cleanly. Check
                    the extracted text below and edit as needed.
                  </span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-border" />
              <span>or paste content below</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            {/* Paste area */}
            <div className="space-y-1.5">
              <Label htmlFor="paste-area">Paste your program content</Label>
              <Textarea
                id="paste-area"
                rows={8}
                placeholder="Paste your program or unit plan here…"
                value={pastedText}
                onChange={(e) => {
                  setPastedText(e.target.value);
                  if (pasteWarning) setPasteWarning(null);
                }}
                onPaste={handlePaste}
                className="font-mono text-sm resize-none"
              />
              {pasteWarning ? (
                <div className="flex gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5">
                  <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>{pasteWarning}</span>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Tables pasted from Word or Google Docs are preserved automatically.
                </p>
              )}
            </div>

            {/* Rule-based split modes */}
            <div className="space-y-2">
              <Label>Split manually into items</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {RULE_BASED_MODES.map((mode) => (
                  <button
                    key={mode.value}
                    type="button"
                    onClick={() => setSplitMode(mode.value)}
                    className={`text-left p-3 rounded-lg border transition-colors ${
                      splitMode === mode.value
                        ? "border-primary bg-primary/5"
                        : "border-input hover:bg-muted/50"
                    }`}
                  >
                    <div className="text-sm font-medium">{mode.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      {mode.description}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* AI-assisted option */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <div className="h-px flex-1 bg-border" />
                <span>or</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <button
                type="button"
                onClick={() => setSplitMode("ai")}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                  isAiMode
                    ? "border-violet-400 bg-violet-50"
                    : "border-input hover:bg-muted/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Sparkles
                    className={`h-4 w-4 flex-shrink-0 ${
                      isAiMode ? "text-violet-600" : "text-muted-foreground"
                    }`}
                  />
                  <span
                    className={`text-sm font-medium ${isAiMode ? "text-violet-900" : ""}`}
                  >
                    AI-assisted import
                  </span>
                </div>
                <p
                  className={`text-xs mt-0.5 ml-6 ${
                    isAiMode ? "text-violet-700" : "text-muted-foreground"
                  }`}
                >
                  AI suggests structured items with content, resources, outcomes, and
                  assessment checkpoints. Requires active subscription.
                </p>
              </button>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              {isAiMode ? (
                <Button
                  type="button"
                  onClick={handleAiParse}
                  disabled={!canProceed || aiParseItems.isPending}
                >
                  {aiParseItems.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analysing…
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Analyse with AI
                    </>
                  )}
                </Button>
              ) : (
                <Button type="button" onClick={handleParse} disabled={!canProceed}>
                  Preview items →
                </Button>
              )}
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            {/* Summary bar */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {draftItems.length} item{draftItems.length !== 1 ? "s" : ""} to import
                {draftItems.length === 0 && (
                  <span className="text-destructive ml-1">— add at least one item</span>
                )}
              </span>
              <button
                type="button"
                onClick={handleReset}
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Back to paste
              </button>
            </div>

            {/* Draft list */}
            {draftItems.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm border rounded-lg">
                All items deleted — go back to paste and try again.
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[40vh] overflow-y-auto pr-1">
                {draftItems.map((item, idx) => (
                  <DraftItemRow
                    key={item.id}
                    item={item}
                    index={idx}
                    total={draftItems.length}
                    onChange={handleChange}
                    onDelete={handleDelete}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    onSplit={handleSplit}
                    onMergeDown={handleMergeDown}
                  />
                ))}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={bulkCreate.isPending || draftItems.length === 0}
              >
                {bulkCreate.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Import {draftItems.length} item{draftItems.length !== 1 ? "s" : ""}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
